import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const FITNESS_GOALS = [
  { id: 'weight_loss', label: 'Weight Loss', icon: 'TrendingDown' },
  { id: 'muscle_gain', label: 'Muscle Gain', icon: 'Dumbbell' },
  { id: 'endurance', label: 'Endurance', icon: 'Activity' },
  { id: 'flexibility', label: 'Flexibility', icon: 'Zap' },
  { id: 'strength', label: 'Strength', icon: 'Shield' },
  { id: 'general_fitness', label: 'General Fitness', icon: 'Heart' },
];

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' },
];

const AddClientModal = ({ isOpen, onClose, trainerId, onClientAdded }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    fitnessGoals: [],
    availableDays: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const toggleGoal = (goalId) => {
    setFormData(prev => ({
      ...prev,
      fitnessGoals: prev?.fitnessGoals?.includes(goalId)
        ? prev?.fitnessGoals?.filter(g => g !== goalId)
        : [...prev?.fitnessGoals, goalId],
    }));
  };

  const toggleDay = (dayId) => {
    setFormData(prev => ({
      ...prev,
      availableDays: prev?.availableDays?.includes(dayId)
        ? prev?.availableDays?.filter(d => d !== dayId)
        : [...prev?.availableDays, dayId],
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.fullName?.trim()) {
      setError('Please enter the client\'s full name.');
      return;
    }
    if (!formData?.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Check if a user profile with this email already exists
      const { data: existingProfile } = await supabase?.from('user_profiles')?.select('id, email, full_name, role')?.eq('email', formData?.email?.trim()?.toLowerCase())?.single();

      let clientId;

      if (existingProfile) {
        clientId = existingProfile?.id;
        // Update fitness_goals if provided
        if (formData?.fitnessGoals?.length > 0) {
          await supabase?.from('user_profiles')?.update({ fitness_goals: formData?.fitnessGoals })?.eq('id', clientId);
        }
      } else {
        // Create a new auth user via admin invite (uses signUp with a temp password)
        const tempPassword = Math.random()?.toString(36)?.slice(-10) + 'A1!';
        const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
          email: formData?.email?.trim()?.toLowerCase(),
          password: tempPassword,
          options: {
            data: {
              full_name: formData?.fullName?.trim(),
              role: 'client',
            },
          },
        });

        if (signUpError) {
          throw new Error(signUpError.message);
        }

        clientId = signUpData?.user?.id;

        if (!clientId) {
          throw new Error('Failed to create client account. Please try again.');
        }

        // Upsert user profile
        const { error: profileError } = await supabase?.from('user_profiles')?.upsert({
            id: clientId,
            email: formData?.email?.trim()?.toLowerCase(),
            full_name: formData?.fullName?.trim(),
            role: 'client',
            fitness_goals: formData?.fitnessGoals,
          }, { onConflict: 'id' });

        if (profileError) {
          throw new Error(profileError.message);
        }
      }

      // Create pt_client_assignment
      const { error: assignError } = await supabase?.from('pt_client_assignments')?.upsert({
          trainer_id: trainerId,
          client_id: clientId,
          status: 'active',
          notes: formData?.availableDays?.length > 0
            ? `Available days: ${formData?.availableDays?.join(', ')}`
            : null,
        }, { onConflict: 'trainer_id,client_id' });

      if (assignError) {
        throw new Error(assignError.message);
      }

      setSuccess(true);
      setTimeout(() => {
        onClientAdded?.(clientId);
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ fullName: '', email: '', fitnessGoals: [], availableDays: [] });
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="UserPlus" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Add New Client</h2>
                <p className="text-xs text-muted-foreground">Quick client onboarding</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Success State */}
          {success ? (
            <div className="p-8 flex flex-col items-center justify-center text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4"
              >
                <Icon name="CheckCircle" size={32} className="text-green-500" />
              </motion.div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Client Added!</h3>
              <p className="text-sm text-muted-foreground">
                {formData?.fullName} has been added to your client list.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <Icon name="AlertCircle" size={16} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              {/* Name & Email */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData?.fullName}
                    onChange={handleInputChange}
                    placeholder="e.g. Jane Smith"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="e.g. jane@example.com"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors text-sm"
                  />
                </div>
              </div>

              {/* Fitness Goals */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Fitness Goals
                  <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {FITNESS_GOALS?.map(goal => {
                    const isSelected = formData?.fitnessGoals?.includes(goal?.id);
                    return (
                      <button
                        key={goal?.id}
                        type="button"
                        onClick={() => toggleGoal(goal?.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                        }`}
                      >
                        <Icon name={goal?.icon} size={15} />
                        {goal?.label}
                        {isSelected && (
                          <Icon name="Check" size={13} className="ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Available Days */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Available Days
                  <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                </label>
                <div className="flex gap-2 flex-wrap">
                  {DAYS_OF_WEEK?.map(day => {
                    const isSelected = formData?.availableDays?.includes(day?.id);
                    return (
                      <button
                        key={day?.id}
                        type="button"
                        onClick={() => toggleDay(day?.id)}
                        className={`w-12 h-10 rounded-lg border text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'bg-background border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                        }`}
                      >
                        {day?.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Adding...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Icon name="UserPlus" size={16} />
                      Add Client
                    </span>
                  )}
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddClientModal;
